import { getCurrentCulture } from "owa-localize";
import type { ClientSearchSortOrder } from "../store/schema/SearchTableQuery";

/**
 * Collator that orders sender names the way the sender sort does: ignoring case and accents and
 * ordering embedded numbers naturally.
 */
export function createSenderNameCollator(): Intl.Collator {
    return new Intl.Collator(getCurrentCulture() || undefined, {
        sensitivity: "base",
        numeric: true,
    });
}

/**
 * Sorts rows by sender name: A to Z for 'Sender', Z to A for 'SenderDescending'. Comparison ignores
 * case and accents and orders embedded numbers naturally. Rows without a sender go last in either
 * direction. The sort is stable, so rows from the same sender keep their relative order - search
 * results arrive newest first, so each sender's mail stays newest first.
 *
 * Names that compare equal but are not identical (for example "Alice" and "alice") are ordered
 * apart by their exact text, so rows with the same exact name - which is what sender group headers
 * key on - always end up next to each other.
 *
 * @returns a new array; the input is not modified
 */
export default function sortRowsBySender<T>(rows: readonly T[], getSenderName: (row: T) => string | undefined, sortOrder: ClientSearchSortOrder): T[] {
    const locale = getCurrentCulture() || undefined;
    const collator = createSenderNameCollator();
    const variantCollator = new Intl.Collator(locale, { sensitivity: "variant", numeric: true });
    const direction = sortOrder === "SenderDescending" ? -1 : 1;
    
    return rows
        .map(row => {
        const rawName = getSenderName(row) ?? "";
        return { row, rawName, senderName: rawName.trim() };
    })
        .sort((a, b) => {
        if (!a.senderName || !b.senderName) {
            return (a.senderName ? 0 : 1) - (b.senderName ? 0 : 1);
        }
        
        const comparison = collator.compare(a.senderName, b.senderName) ||
            variantCollator.compare(a.senderName, b.senderName) ||
            (a.rawName < b.rawName ? -1 : a.rawName > b.rawName ? 1 : 0);
        
        return direction * comparison;
    })
        .map(({ row }) => row);
}
